const API_BASE =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:8000");

const TOKEN_KEY = "auth_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

// Header di autenticazione da unire alle richieste
export function authHeaders() {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Gestione centralizzata del 401: svuota il token e notifica l'app (torna al login)
function handleUnauthorized() {
  setToken(null);
  window.dispatchEvent(new Event("auth:logout"));
}

async function request(method, path, body) {
  const opts = {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders() },
  };
  if (body !== undefined) opts.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, opts);
  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("Sessione scaduta");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || "Errore di rete");
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  // Commesse
  getCommesse: (tutti = false) => request("GET", `/api/commesse/?tutti=${tutti}`),
  createCommessa: (data) => request("POST", "/api/commesse/", data),
  patchCommessa: (id, data) => request("PATCH", `/api/commesse/${id}`, data),
  deleteCommessa: (id) => request("DELETE", `/api/commesse/${id}`),

  // Weeks
  getWeeks: (tutti = false) => request("GET", `/api/weeks/?tutti=${tutti}`),
  createWeek: (data) => request("POST", "/api/weeks/", data),
  updateWeek: (id, data) => request("PATCH", `/api/weeks/${id}`, data),
  deleteWeek: (id) => request("DELETE", `/api/weeks/${id}`),

  // Pianificazione
  getTabella: () => request("GET", "/api/pianificazione/tabella"),
  upsertPianificazione: (data) => request("POST", "/api/pianificazione/upsert", data),
  getPianificazioneLista: () => request("GET", "/api/pianificazione/lista"),
  deletePianificazione: (id) => request("DELETE", `/api/pianificazione/${id}`),
  patchPianificazione: (id, data) => request("PATCH", `/api/pianificazione/${id}`, data),

  // Nominali per reparto
  upsertNominaliReparto: (data) => request("POST", "/api/pianificazione/nominali-reparto", data),

  // Sposta container tra settimane
  spostaContainer: (data) => request("POST", "/api/pianificazione/sposta-container", data),

  // Dettaglio / Consuntivo unificato
  getDettaglioConsuntivo: (weekId) => request("GET", `/api/pianificazione/dettaglio-consuntivo?week_id=${weekId}`),
  getTuttiContainers: (weekId) => request("GET", `/api/pianificazione/tutti-containers?week_id=${weekId}`),
  upsertConsuntivo: (data) => request("POST", "/api/pianificazione/consuntivo/upsert", data),

  // Import colli prodotti da Excel
  importExcelPreview: async ({ file, commessa_id, container, week_id }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("commessa_id", commessa_id);
    fd.append("container", container);
    fd.append("week_id", week_id);
    const res = await fetch(`${API_BASE}/api/pianificazione/consuntivo/import-excel/preview`, {
      method: "POST",
      body: fd,
      headers: { ...authHeaders() },
    });
    if (res.status === 401) {
      handleUnauthorized();
      throw new Error("Sessione scaduta");
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Errore di rete");
    }
    return res.json();
  },
  importExcelApply: (data) => request("POST", "/api/pianificazione/consuntivo/import-excel/apply", data),

  getRiepilogoWeek: (weekId) => request("GET", `/api/pianificazione/riepilogo-week?week_id=${weekId}`),

  // Riepilogo giornaliero (tab Riepilogo)
  getRiepilogo: () => request("GET", "/api/riepilogo/"),
  upsertRiepilogo: (data) => request("POST", "/api/riepilogo/upsert", data),

  // Import CSV colli nel Riepilogo (preview: nessuna scrittura)
  importRiepilogoCsvPreview: async (fileList) => {
    const fd = new FormData();
    for (const f of fileList) fd.append("files", f);
    const res = await fetch(`${API_BASE}/api/riepilogo/import-csv/preview`, {
      method: "POST",
      body: fd,
      headers: { ...authHeaders() },
    });
    if (res.status === 401) { handleUnauthorized(); throw new Error("Sessione scaduta"); }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Errore di rete");
    }
    return res.json();
  },
  aggiungiDataRiepilogo: (data) => request("POST", "/api/riepilogo/aggiungi", { data }),
  eliminaDataRiepilogo: (data) => request("DELETE", `/api/riepilogo/${data}`),

  // Operatori
  getOperatori: (tutti = false) => request("GET", `/api/operatori/?tutti=${tutti}`),
  createOperatore: (data) => request("POST", "/api/operatori/", data),
  updateOperatore: (id, data) => request("PATCH", `/api/operatori/${id}`, data),
  deleteOperatore: (id) => request("DELETE", `/api/operatori/${id}`),

  // Reparti
  getReparti: (tutti = false) => request("GET", `/api/reparti/?tutti=${tutti}`),
  createReparto: (data) => request("POST", "/api/reparti/", data),
  updateReparto: (id, data) => request("PATCH", `/api/reparti/${id}`, data),
  deleteReparto: (id) => request("DELETE", `/api/reparti/${id}`),

  // Autenticazione / utenti / log
  login: (username, password) => request("POST", "/api/auth/login", { username, password }),
  me: () => request("GET", "/api/auth/me"),
  cambiaPassword: (password_attuale, password_nuova) =>
    request("POST", "/api/auth/cambia-password", { password_attuale, password_nuova }),
  getUtenti: () => request("GET", "/api/auth/utenti"),
  createUtente: (data) => request("POST", "/api/auth/utenti", data),
  updateUtente: (id, data) => request("PATCH", `/api/auth/utenti/${id}`, data),
  deleteUtente: (id) => request("DELETE", `/api/auth/utenti/${id}`),
  getLog: (limit = 500) => request("GET", `/api/auth/log?limit=${limit}`),
};
